import React from 'react';
import { ScrollView, Text, TextInput, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { ReportCategory } from '../../../AnalyticsHub/pages/Reports/reportCatalog';
import { ReportStatus } from '../../../../store/slices/reportingConfigurationSlice';
import { useTheme } from '../../../../theme/useTheme';
import { AdminColors } from '../../constants/adminColors';
import { ColumnFilters, FilterKey, REPORTING_PRIMARY_TAG_OPTIONS } from '../../constants/reportingConfiguration';
import styles from './ReportingConfiguration.styles';

interface ReportingConfigurationFilterContentProps {
  filterKey: FilterKey;
  columnFilters: ColumnFilters;
  uniqueSecondaryTags: ReportCategory[];
  uniqueKpiTags: string[];
  setTextColumnFilter: (key: 'nameQuery' | 'descriptionQuery' | 'linkQuery', value: string) => void;
  toggleListFilter: <T extends string>(
    key: 'primaryTags' | 'secondaryTags' | 'kpiTags' | 'statuses',
    value: T
  ) => void;
  clearSingleFilter: (key: FilterKey) => void;
  onClose: () => void;
}

const ReportingConfigurationFilterContent: React.FC<ReportingConfigurationFilterContentProps> = ({
  filterKey,
  columnFilters,
  uniqueSecondaryTags,
  uniqueKpiTags,
  setTextColumnFilter,
  toggleListFilter,
  clearSingleFilter,
  onClose,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const renderListOption = (label: string, checked: boolean, onPress: () => void) => (
    <TouchableOpacity key={label} style={styles.filterOptionRow} onPress={onPress}>
      <MaterialIcons
        name={checked ? 'check-box' : 'check-box-outline-blank'}
        size={16}
        color={checked ? colors.brand.primary : (isDark ? colors.text.disabled : AdminColors.gray300)}
      />
      <Text style={[styles.filterOptionText, isDark && { color: colors.text.primary }]}>{label}</Text>
    </TouchableOpacity>
  );

  if (filterKey === 'name' || filterKey === 'description' || filterKey === 'link') {
    const value =
      filterKey === 'name'
        ? columnFilters.nameQuery
        : filterKey === 'description'
          ? columnFilters.descriptionQuery
          : columnFilters.linkQuery;
    const inputKey =
      filterKey === 'name' ? 'nameQuery' : filterKey === 'description' ? 'descriptionQuery' : 'linkQuery';

    return (
      <View>
        <TextInput
          value={value}
          onChangeText={(text) => setTextColumnFilter(inputKey, text)}
          style={[styles.filterTextInput, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary }]}
          placeholder="Type to filter..."
          placeholderTextColor={isDark ? colors.text.disabled : AdminColors.gray350}
          autoFocus
        />
        <View style={[styles.filterActionsRow, isDark && { borderTopColor: colors.border.divider }]}>
          <TouchableOpacity onPress={() => clearSingleFilter(filterKey)}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Clear</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (filterKey === 'primaryTag') {
    return (
      <View>
        <View style={styles.filterOptionsList}>
          {REPORTING_PRIMARY_TAG_OPTIONS.map((tag) =>
            renderListOption(tag, columnFilters.primaryTags.includes(tag), () => toggleListFilter('primaryTags', tag))
          )}
        </View>
        <View style={[styles.filterActionsRow, isDark && { borderTopColor: colors.border.divider }]}>
          <TouchableOpacity onPress={() => clearSingleFilter(filterKey)}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Clear</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (filterKey === 'secondaryTags') {
    return (
      <View>
        <View style={styles.filterOptionsList}>
          {uniqueSecondaryTags.map((tag) =>
            renderListOption(tag, columnFilters.secondaryTags.includes(tag), () => toggleListFilter('secondaryTags', tag))
          )}
        </View>
        <View style={[styles.filterActionsRow, isDark && { borderTopColor: colors.border.divider }]}>
          <TouchableOpacity onPress={() => clearSingleFilter(filterKey)}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Clear</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (filterKey === 'kpiTags') {
    return (
      <View>
        <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled showsVerticalScrollIndicator>
          <View style={styles.filterOptionsList}>
            {uniqueKpiTags.map((tag) =>
              renderListOption(tag, columnFilters.kpiTags.includes(tag), () => toggleListFilter('kpiTags', tag))
            )}
          </View>
        </ScrollView>
        <View style={[styles.filterActionsRow, isDark && { borderTopColor: colors.border.divider }]}>
          <TouchableOpacity onPress={() => clearSingleFilter(filterKey)}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Clear</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose}>
            <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View>
      <View style={styles.filterOptionsList}>
        {(['Active', 'Disabled'] as ReportStatus[]).map((status) =>
          renderListOption(status, columnFilters.statuses.includes(status), () => toggleListFilter('statuses', status))
        )}
      </View>
      <View style={[styles.filterActionsRow, isDark && { borderTopColor: colors.border.divider }]}>
        <TouchableOpacity onPress={() => clearSingleFilter(filterKey)}>
          <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Clear</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onClose}>
          <Text style={[styles.filterActionText, isDark && { color: colors.brand.primary }]}>Apply</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default ReportingConfigurationFilterContent;
